import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Sensors as SensorsIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import axios from 'axios';
import VirtualMotorCard from './VirtualMotorCard';
import { API_BASE_URL } from '../../../config/config';

// --- Helper Component for Tab Panels ---
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{ pt: 3, pb: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// --- Read-Only Motor Group Card Component ---
const ReadOnlyMotorGroupCard = ({ group, onStart, onStop, onClearPressureError, actionLoading }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running':
      case 'on': return 'success';
      case 'starting': return 'info';
      case 'stopping': return 'warning';
      case 'error': return 'error';
      case 'pressure_error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'running':
      case 'on': return <CheckCircleIcon color="success" fontSize="small" />;
      case 'starting': return <CircularProgress size={14} />;
      case 'stopping': return <CircularProgress size={14} />;
      case 'error': return <ErrorIcon color="error" fontSize="small" />;
      case 'pressure_error': return <WarningIcon color="error" fontSize="small" />;
      default: return null;
    }
  };

  const isRunningOrStarting = ['RUNNING', 'STARTING', 'ON'].includes(group.status?.toUpperCase());
  const isBusy = actionLoading || group.status?.toUpperCase() === 'STARTING' || group.status?.toUpperCase() === 'STOPPING';

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: `1px solid ${getStatusColor(group.status) === 'default' ? '#e0e0e0' : 'transparent'}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#1a237e', wordBreak: 'break-word', pr: 1 }}>
            {group.name}
          </Typography>
          <Chip
            label={group.status?.toUpperCase() || 'IDLE'}
            color={getStatusColor(group.status)}
            size="small"
            icon={getStatusIcon(group.status)}
            sx={{ fontWeight: 'bold', minWidth: 80 }}
          />
        </Box>

        {group.description && (
          <Typography variant="body2" color="textSecondary" mb={2} sx={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
            {group.description}
          </Typography>
        )}

        {(group.lastStarted || group.lastStopped) && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {isRunningOrStarting && group.lastStarted && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50', display: 'inline-block' }}></span>
                <strong>Last Started:</strong>&nbsp;{new Date(group.lastStarted).toLocaleString()}
              </Typography>
            )}
            {!isRunningOrStarting && group.lastStopped && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f44336', display: 'inline-block' }}></span>
                <strong>Last Stopped:</strong>&nbsp;{new Date(group.lastStopped).toLocaleString()}
              </Typography>
            )}
            {!isRunningOrStarting && !group.lastStopped && group.lastStarted && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50', display: 'inline-block' }}></span>
                <strong>Last Started:</strong>&nbsp;{new Date(group.lastStarted).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ bgcolor: '#f5f7fa', borderRadius: 2, p: 1.5, mb: 1 }}>
          <Typography variant="caption" fontWeight="bold" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
            SEQUENCE ({group.startupSequence?.length || 0} MOTORS)
          </Typography>
          <List dense disablePadding>
            {group.startupSequence?.map((motor, index) => (
              <ListItem
                key={motor._id || index}
                disableGutters
                sx={{ py: 0.5, borderBottom: index !== group.startupSequence.length - 1 ? '1px dashed #e0e0e0' : 'none' }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={index === group.mainMotorIndex ? 'bold' : 'medium'} sx={{ fontSize: '0.85rem' }}>
                      {index + 1}. {motor.motorName || motor.motorId?.name || 'Unknown Motor'}
                      {index === group.mainMotorIndex && (
                        <Chip label="MAIN" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', ml: 1 }} />
                      )}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="textSecondary">
                      Delay: {motor.onDelaySeconds || 0}s {motor.startCondition?.type ? ' • Conditional Start' : ''}
                    </Typography>
                  }
                />
                <Chip
                  label={motor.motorId?.status || 'Unknown'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: motor.motorId?.status === 'ON' ? '#e8f5e9' : motor.motorId?.status === 'OFF' ? '#ffebee' : '#f5f5f5',
                    color: motor.motorId?.status === 'ON' ? '#2e7d32' : motor.motorId?.status === 'OFF' ? '#c62828' : '#757575',
                    fontWeight: 'bold'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {group.pressureError && (
          <Alert severity="error" sx={{ mt: 2, py: 0, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
            Pressure Error: {group.pressureErrorMessage || 'Out of range'}
          </Alert>
        )}
      </CardContent>

      <Box sx={{ px: 2, pb: 2, pt: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', mt: 'auto' }}>
        <FormControlLabel
          control={
            <Switch
              checked={isRunningOrStarting}
              onChange={() => isRunningOrStarting ? onStop(group._id) : onStart(group._id)}
              disabled={isBusy}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" fontWeight="bold" color={isRunningOrStarting ? 'primary.main' : 'text.secondary'}>
              {isRunningOrStarting ? 'GROUP ON' : 'GROUP OFF'}
            </Typography>
          }
          sx={{ ml: 0, mt: 1 }}
        />

        {group.pressureError && (
          <Tooltip title="Clear Pressure Error">
            <IconButton color="warning" onClick={() => onClearPressureError(group._id)} size="small" sx={{ mt: 1, bgcolor: '#fff3e0' }}>
              <WarningIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
};

// --- Main Grid Component ---
const VirtualMotorsGrid = ({ virtualMotors, deviceCode, fetchVirtualMotors }) => {
  const [tabValue, setTabValue] = useState(0);

  // Group states
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch groups function
  const fetchGroups = useCallback(async () => {
    if (!deviceCode) return;
    try {
      setLoadingGroups(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}api/virtual/motor-group/${deviceCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setGroups(response.data.groups || []);
      }
    } catch (error) {
      console.error('Error fetching motor groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  }, [deviceCode]);

  // Fetch groups initially and when deviceCode changes
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Auto-refresh groups every 30s while viewing the grid
  useEffect(() => {
    const interval = setInterval(() => {
      fetchGroups();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchGroups]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      fetchGroups(); // Refresh groups immediately when tab is clicked
    }
  };

  const getAuthHeaders = () => {
    return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  };

  const handleStartGroup = async (groupId) => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}api/virtual/motor-group/${groupId}/start`, {}, getAuthHeaders());
      fetchGroups();
    } catch (error) {
      console.error('Error starting motor group:', error);
      alert(error.response?.data?.message || 'Error starting motor group');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopGroup = async (groupId) => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}api/virtual/motor-group/${groupId}/stop`, {}, getAuthHeaders());
      fetchGroups();
    } catch (error) {
      console.error('Error stopping motor group:', error);
      alert(error.response?.data?.message || 'Error stopping motor group');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearPressureError = async (groupId) => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}api/virtual/motor-group/${groupId}/clear-pressure-error`, {}, getAuthHeaders());
      fetchGroups();
    } catch (error) {
      console.error('Error clearing pressure error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 4, width: '100%' }}>
      {/* Tabs Header */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fff', borderRadius: '12px 12px 0 0', px: { xs: 1, sm: 2 }, pt: 1 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              textTransform: 'none',
              py: 1.5
            }
          }}
        >
          <Tab icon={<SensorsIcon fontSize="small" />} iconPosition="start" label="Individual Motors" />
          <Tab icon={<ViewModuleIcon fontSize="small" />} iconPosition="start" label="Motor Groups" />
        </Tabs>
      </Box>

      {/* Tab 1: Individual Motors */}
      <CustomTabPanel value={tabValue} index={0}>
        {!virtualMotors || virtualMotors.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 4, border: '1px dashed #cfd8dc' }}>
            <Typography variant="body1" color="text.secondary">
              No individual virtual motors configured.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent={{ xs: 'center', md: 'flex-start' }}>
            {[...virtualMotors].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((motor) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={motor._id}>
                <VirtualMotorCard
                  motor={motor}
                  deviceCode={deviceCode}
                  onRefresh={() => fetchVirtualMotors(deviceCode)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </CustomTabPanel>

      {/* Tab 2: Motor Groups */}
      <CustomTabPanel value={tabValue} index={1}>
        {loadingGroups && groups.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading groups...
            </Typography>
          </Box>
        ) : !groups || groups.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 4, border: '1px dashed #cfd8dc' }}>
            <Typography variant="body1" color="text.secondary">
              No motor groups configured for this device.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Motor groups handle sequential startup of multiple motors (e.g., Valve → Pump).
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {groups.map((group) => (
              <Grid item xs={12} sm={6} md={4} key={group._id}>
                <ReadOnlyMotorGroupCard
                  group={group}
                  onStart={handleStartGroup}
                  onStop={handleStopGroup}
                  onClearPressureError={handleClearPressureError}
                  actionLoading={actionLoading}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </CustomTabPanel>
    </Box>
  );
};

export default VirtualMotorsGrid;
